/**
 * 店铺卡片组件
 */

import React, { FC } from 'react';
import { Shop } from '../../types';
import FeatureTag from './FeatureTag';

export interface ShopCardProps {
  shop: Shop;
  onBookmark: (shopId: string) => void;
  onReserve: (club: any) => void;
  isMapVersion: boolean;
}

const ShopCard: FC<ShopCardProps> = ({ shop, onBookmark, onReserve, isMapVersion = false }) => {
  const handleReserve = (e: React.MouseEvent) => {
    e.preventDefault();
    onReserve({
      club_id: shop.id,
      club_name: shop.name,
      image: shop.image,
      businessHours: shop.businessHours,
      phone: shop.phone,
      address: shop.address,
      annexOpenDate: shop.annexOpenDate || null,
      annexAddress: shop.annexAddress || null,
      features: shop.features.map((feature) => feature.icon),
      clubPicture: shop.clubPicture || null,
      preOpenDay: shop.preOpenDay || null,
    });
  };

  const cardRef = React.useRef<HTMLDivElement>(null);

  // 地图版本时强制应用样式
  React.useEffect(() => {
    if (isMapVersion && cardRef.current) {
      const element = cardRef.current;
      element.style.setProperty('background-color', '#ffffff', 'important');
      element.style.setProperty('border-radius', '10px', 'important');
      element.style.setProperty('border', 'none', 'important');
      element.style.setProperty('box-shadow', '0 5px 4px 0 rgba(0, 0, 0, 0.15)', 'important');
    }
  }, [isMapVersion]);

  return (
    <div ref={cardRef} className={`p-store-card ${isMapVersion ? 'p-store-card--map' : ''}`}>
      <button
        type="button"
        className="p-store-card__full-link"
        onClick={handleReserve}
        aria-label={`${shop.name}で予約する`}
      />

      {/* 店铺名称和收藏按钮 */}
      <div className="p-store-card__name p-store-card__name--mark">{shop.name}</div>

      {/* 店铺主体内容 */}
      <div className="p-store-card__body">
        {/* 店铺图片 */}

        <picture className="p-store-card__picture">
          <img loading="lazy" className="p-store-card__image" src={`${shop.image}`} alt={shop.name} />
        </picture>

        {/* 店铺信息 */}
        <div className="p-store-card__info">
          <p className="p-store-card__info-item" style={{ whiteSpace: 'pre-line' }}>
            <img src="/assets/img/icon-clock.svg" alt="営業時間" className="p-store-card__info-icon" />
            {shop.businessHours.replace(/\n+/g, '\n')}
          </p>
          <p className="p-store-card__info-item">
            <img src="/assets/img/icon-phone.svg" alt="電話番号" className="p-store-card__info-icon" />
            <a href={`tel:${shop.phone ? shop.phone.replace(/-/g, '') : '#'}`} className="p-store-card__info-link">
              {shop.phone || ''}
            </a>
          </p>
          <p className="p-store-card__info-item">
            <img src="/assets/img/icon-map-black.svg" alt="住所" className="p-store-card__info-icon" />
            {shop.address || ''}
          </p>
          {shop.annexOpenDate && (
            <p className="p-store-card__info-item" style={{ whiteSpace: 'pre-line' }}>
              <img src="/assets/img/icon-clock.svg" alt="別館営業時間" className="p-store-card__info-icon" />
              {shop.annexOpenDate && `(別館)${shop.annexOpenDate.replace(/\n+/g, '\n')}`}
            </p>
          )}
          {shop.annexAddress && (
            <p className="p-store-card__info-item">
              <img src="/assets/img/icon-map-black.svg" alt="別館住所" className="p-store-card__info-icon" />
              {shop.annexAddress && `(別館)${shop.annexAddress.replace(/\n+/g, '\n')}`}
            </p>
          )}
        </div>
      </div>

      {/* 设施标签 */}
      <div className="p-store-card__features">
        {shop.features.map((feature) => (
          <FeatureTag key={feature.id} feature={feature} />
        ))}
      </div>

      {/* 预约链接 */}
      <button type="button" className="p-store-card__reservation" onClick={handleReserve}>
        この店舗で予約する
        <img src="/assets/img/icon-arrow-right-red.svg" alt="画像" className="p-icon p-icon--size14" />
      </button>
    </div>
  );
};

export default ShopCard;
